import { useId } from 'react'

import { compact, layout } from './chart'

const W = 640
const H = 170
const PAD = { left: 40, right: 12, top: 10, bottom: 22 }

export interface LineChartProps {
  title: string
  points: readonly { t: string; v: number }[]
  from: string
  to: string
  /** Formats the latest/peak figures in the caption, e.g. bytes. */
  format?: (value: number) => string
  step: 'minute' | 'hour'
}

function tickLabel(t: number, step: 'minute' | 'hour', spanMs: number): string {
  const d = new Date(t)
  if (spanMs > 2 * 86_400_000) return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: step === 'minute' ? '2-digit' : undefined })
}

/**
 * A small, dependency-free SVG line chart: one series, a faint area, three ticks per axis and the
 * latest point marked. Colours come from the theme tokens so it follows light and dark. The figures
 * are repeated as text in the caption and the accessible name, so nothing is conveyed by the line alone.
 */
export function LineChart({ title, points, from, to, format = compact, step }: LineChartProps) {
  const id = useId()
  const fromMs = Date.parse(from)
  const toMs = Date.parse(to)
  const numeric = points.map((p) => ({ t: Date.parse(p.t), v: p.v })).filter((p) => Number.isFinite(p.t))
  const g = layout(numeric, W, H, PAD, fromMs, toMs)
  const latest = numeric.length === 0 ? undefined : numeric.reduce((a, b) => (b.t >= a.t ? b : a))
  const peak = numeric.reduce((m, p) => Math.max(m, p.v), 0)
  const caption =
    latest === undefined
      ? 'No data in this range yet.'
      : `Latest ${format(latest.v)}, peak ${format(peak)}`

  return (
    <figure className="min-w-0 space-y-1.5" data-testid="staff-chart">
      <figcaption className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{caption}</span>
      </figcaption>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-labelledby={id}
        className="h-auto w-full text-primary"
       
      >
        <title id={id}>{`${title}. ${caption}`}</title>
        {g.yTicks.map((tick) => (
          <g key={tick.value}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={tick.y}
              y2={tick.y}
              stroke="var(--border)"
              strokeWidth={1}
            />
            <text x={PAD.left - 6} y={tick.y + 3} textAnchor="end" fontSize={10} fill="var(--muted-foreground)">
              {compact(tick.value)}
            </text>
          </g>
        ))}
        {g.xTicks.map((tick) => (
          <text
            key={tick.t}
            x={tick.x}
            y={H - 6}
            textAnchor={tick.x <= PAD.left + 1 ? 'start' : tick.x >= W - PAD.right - 1 ? 'end' : 'middle'}
            fontSize={10}
            fill="var(--muted-foreground)"
          >
            {tickLabel(tick.t, step, toMs - fromMs)}
          </text>
        ))}
        {g.area === '' ? null : <path d={g.area} fill="currentColor" opacity={0.1} />}
        {g.line === '' ? null : (
          <path d={g.line} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinejoin="round" />
        )}
        {g.last === null ? null : <circle cx={g.last.x} cy={g.last.y} r={3} fill="currentColor" />}
      </svg>
    </figure>
  )
}
